import React, { useMemo } from 'react';
import { Skeleton, Tabs } from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { SalesMonthlyTrend } from '../../../types/dashboard';
import { formatNumber, formatRupiah, MONTH_NAMES } from '../utils';

interface SalesDetailTrendsChartProps {
  trends: SalesMonthlyTrend[] | null;
  loading: boolean;
  glassTableContainerStyle: React.CSSProperties;
}

const SalesDetailTrendsChart: React.FC<SalesDetailTrendsChartProps> = ({
  trends,
  loading,
  glassTableContainerStyle
}) => {
  // Monthly (MoM) Chart Options
  const trendsChartOptions = useMemo(() => {
    if (!trends) return {};

    const categories = MONTH_NAMES;
    const omzetTargetData = MONTH_NAMES.map((_, i) => {
      const match = trends.find(t => t.month === i + 1);
      return match ? match.omzet_target : 0;
    });
    const omzetRealizationData = MONTH_NAMES.map((_, i) => {
      const match = trends.find(t => t.month === i + 1);
      return match ? match.omzet_realization : 0;
    });

    const leadsTargetData = MONTH_NAMES.map((_, i) => {
      const match = trends.find(t => t.month === i + 1);
      return match ? match.leads_target : 0;
    });
    const leadsRealizationData = MONTH_NAMES.map((_, i) => {
      const match = trends.find(t => t.month === i + 1);
      return match ? match.leads_realization : 0;
    });

    return {
      chart: {
        zoomType: 'xy',
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' },
        height: 350,
      },
      title: {
        text: 'Korelasi Target & Realisasi Bulanan (Omzet vs Leads)',
        align: 'left',
        style: { fontSize: '14px', fontWeight: 'bold', color: '#1e293b' }
      },
      xAxis: [{
        categories,
        crosshair: true,
        labels: { style: { color: '#64748b' } },
        lineColor: '#cbd5e1'
      }],
      yAxis: [{ // Primary yAxis (Leads)
        min: 0,
        title: { text: 'Jumlah Leads', style: { color: '#1890ff' } },
        labels: { style: { color: '#1890ff' } },
        gridLineColor: 'rgba(0, 0, 0, 0.04)',
        gridLineDashStyle: 'Dash',
      }, { // Secondary yAxis (Omzet)
        min: 0,
        title: { text: 'Nominal Omzet (Rp)', style: { color: '#7BAA18' } },
        labels: { style: { color: '#7BAA18' } },
        opposite: true,
        gridLineColor: 'rgba(0, 0, 0, 0.04)',
        gridLineDashStyle: 'Dash',
      }],
      tooltip: {
        shared: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e2e8f0',
        shadow: true,
        formatter: function (this: any) {
          let s = `<span style="font-size: 11px; color: #64748b; font-weight: bold;">${this.x}</span>`;
          this.points.forEach((point: any) => {
            const isOmzet = point.series.name.includes('Omzet');
            const valFormatted = isOmzet ? formatRupiah(point.y) : `${formatNumber(point.y)} Leads`;
            s += `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <span style="font-weight: bold; color: #0f172a;">${valFormatted}</span>`;
          });
          return s;
        }
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: { color: '#475569', fontSize: '12px' }
      },
      colors: ['#cbd5e1', '#7BAA18', '#faad14', '#1890ff'], // OmzetTarget (Slate), OmzetRealization (Emerald), LeadsTarget (Yellow), LeadsRealization (Vibrant Blue)
      credits: { enabled: false },
      plotOptions: {
        column: {
          borderRadius: 6,
          borderWidth: 0,
          maxPointWidth: 16,
          dataLabels: {
            enabled: true,
            formatter: function (this: any) {
              if (this.y === 0) return null;
              const isOmzet = this.series.name.includes('Omzet');
              if (isOmzet) {
                const val = this.y;
                if (val >= 1000000000) {
                  return 'Rp ' + (val / 1000000000).toFixed(1) + 'M';
                }
                if (val >= 1000000) {
                  return 'Rp ' + (val / 1000000).toFixed(0) + 'Jt';
                }
                return 'Rp ' + val.toLocaleString();
              }
              return this.y;
            },
            style: {
              fontSize: '9px',
              fontWeight: 'bold',
              textOutline: 'none'
            }
          }
        },
        spline: {
          lineWidth: 3,
          marker: {
            enabled: true,
            radius: 4,
            symbol: 'circle'
          },
          enableMouseTracking: true,
          dataLabels: {
            enabled: true,
            formatter: function (this: any) {
              if (this.y === 0) return null;
              return this.y;
            },
            style: {
              fontSize: '10px',
              fontWeight: 'bold',
              textOutline: 'none'
            }
          }
        }
      },
      series: [
        {
          name: 'Target Omzet',
          type: 'column',
          yAxis: 1,
          data: omzetTargetData,
          tooltip: { valueSuffix: ' Rp' }
        },
        {
          name: 'Realisasi Omzet',
          type: 'column',
          yAxis: 1,
          data: omzetRealizationData,
          tooltip: { valueSuffix: ' Rp' }
        },
        {
          name: 'Target Leads',
          type: 'spline',
          data: leadsTargetData,
        },
        {
          name: 'Realisasi Leads',
          type: 'spline',
          data: leadsRealizationData,
        }
      ]
    };
  }, [trends]);

  // Year-to-Date (Cumulative) Chart Options
  const cumulativeChartOptions = useMemo(() => {
    if (!trends) return {};

    const categories = MONTH_NAMES;
    const omzetTargetData: number[] = [];
    const omzetRealizationData: number[] = [];
    const leadsTargetData: number[] = [];
    const leadsRealizationData: number[] = [];

    let cumOmzetTarget = 0;
    let cumOmzetRealization = 0;
    let cumLeadsTarget = 0;
    let cumLeadsRealization = 0;

    for (let i = 0; i < 12; i++) {
      const match = trends.find(t => t.month === i + 1);
      cumOmzetTarget += match ? match.omzet_target : 0;
      cumOmzetRealization += match ? match.omzet_realization : 0;
      cumLeadsTarget += match ? match.leads_target : 0;
      cumLeadsRealization += match ? match.leads_realization : 0;

      omzetTargetData.push(cumOmzetTarget);
      omzetRealizationData.push(cumOmzetRealization);
      leadsTargetData.push(cumLeadsTarget);
      leadsRealizationData.push(cumLeadsRealization);
    }

    return {
      chart: {
        zoomType: 'xy',
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' },
        height: 350,
      },
      title: {
        text: 'Tren Akumulasi Kinerja Komulatif YTD (Omzet vs Leads)',
        align: 'left',
        style: { fontSize: '14px', fontWeight: 'bold', color: '#1e293b' }
      },
      xAxis: [{
        categories,
        crosshair: true,
        labels: { style: { color: '#64748b' } },
        lineColor: '#cbd5e1'
      }],
      yAxis: [{ // Primary yAxis (Leads)
        min: 0,
        title: { text: 'Akumulasi Jumlah Leads', style: { color: '#1890ff' } },
        labels: { style: { color: '#1890ff' } },
        gridLineColor: 'rgba(0, 0, 0, 0.04)',
        gridLineDashStyle: 'Dash',
      }, { // Secondary yAxis (Omzet)
        min: 0,
        title: { text: 'Akumulasi Nominal Omzet (Rp)', style: { color: '#7BAA18' } },
        labels: { style: { color: '#7BAA18' } },
        opposite: true,
        gridLineColor: 'rgba(0, 0, 0, 0.04)',
        gridLineDashStyle: 'Dash',
      }],
      tooltip: {
        shared: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e2e8f0',
        shadow: true,
        formatter: function (this: any) {
          let s = `<span style="font-size: 11px; color: #64748b; font-weight: bold;">Akumulasi s.d. ${this.x}</span>`;
          this.points.forEach((point: any) => {
            const isOmzet = point.series.name.includes('Omzet');
            const valFormatted = isOmzet ? formatRupiah(point.y) : `${formatNumber(point.y)} Leads`;
            s += `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <span style="font-weight: bold; color: #0f172a;">${valFormatted}</span>`;
          });
          return s;
        }
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: { color: '#475569', fontSize: '12px' }
      },
      colors: ['#cbd5e1', '#7BAA18', '#faad14', '#1890ff'], // OmzetTarget (Slate), OmzetRealization (Emerald), LeadsTarget (Yellow), LeadsRealization (Vibrant Blue)
      credits: { enabled: false },
      plotOptions: {
        column: {
          borderRadius: 6,
          borderWidth: 0,
          maxPointWidth: 16,
          dataLabels: {
            enabled: true,
            formatter: function (this: any) {
              if (this.y === 0) return null;
              const isOmzet = this.series.name.includes('Omzet');
              if (isOmzet) {
                const val = this.y;
                if (val >= 1000000000) {
                  return 'Rp ' + (val / 1000000000).toFixed(1) + 'M';
                }
                if (val >= 1000000) {
                  return 'Rp ' + (val / 1000000).toFixed(0) + 'Jt';
                }
                return 'Rp ' + val.toLocaleString();
              }
              return this.y;
            },
            style: {
              fontSize: '9px',
              fontWeight: 'bold',
              textOutline: 'none'
            }
          }
        },
        spline: {
          lineWidth: 3,
          marker: {
            enabled: true,
            radius: 4,
            symbol: 'circle'
          },
          enableMouseTracking: true,
          dataLabels: {
            enabled: true,
            formatter: function (this: any) {
              if (this.y === 0) return null;
              return this.y;
            },
            style: {
              fontSize: '10px',
              fontWeight: 'bold',
              textOutline: 'none'
            }
          }
        }
      },
      series: [
        {
          name: 'Target Omzet (Akumulatif)',
          type: 'column',
          yAxis: 1,
          data: omzetTargetData,
          tooltip: { valueSuffix: ' Rp' }
        },
        {
          name: 'Realisasi Omzet (Akumulatif)',
          type: 'column',
          yAxis: 1,
          data: omzetRealizationData,
          tooltip: { valueSuffix: ' Rp' }
        },
        {
          name: 'Target Leads (Akumulatif)',
          type: 'spline',
          data: leadsTargetData,
        },
        {
          name: 'Realisasi Leads (Akumulatif)',
          type: 'spline',
          data: leadsRealizationData,
        }
      ]
    };
  }, [trends]);

  return (
    <div className="glass-table-container" style={{ ...glassTableContainerStyle, marginTop: '20px', padding: '24px' }}>
      {loading || !trends ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : (
        <Tabs
          defaultActiveKey="mom"
          type="line"
          items={[
            {
              key: 'mom',
              label: 'Tren Bulanan (Month-on-Month)',
              children: <HighchartsReact highcharts={Highcharts} options={trendsChartOptions} />
            },
            {
              key: 'cumulative',
              label: 'Tren Akumulatif (Year-to-Date)',
              children: <HighchartsReact highcharts={Highcharts} options={cumulativeChartOptions} />
            }
          ]}
        />
      )}
    </div>
  );
};

export default SalesDetailTrendsChart;
